import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { AlertTriangle, ChevronDown, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import api from '../../../services/api';
import {
  GoogleAccountReport,
  GoogleBiddingRow,
  GoogleCallout,
  errorMessage,
  num,
} from './googleAccountTypes';

type TabKey = 'bidding' | 'callouts';

interface Props {
  report: GoogleAccountReport | null;
  reportLoading: boolean;
  reportError: string | null;
  onReloadReport: () => void;
  range: { startDate: string; endDate: string } | null;
  showValues: boolean;
}

// v2: o painel passou a abrir fechado (os números da conta ficam no cabeçalho)
const OPEN_KEY = 'campaigns.googleAccountPanel.open.v2';
const TAB_KEY = 'campaigns.googleAccountPanel.tab';
const CALLOUT_MAX = 25;

const readLS = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const writeLS = (key: string, value: string) => {
  try { localStorage.setItem(key, value); } catch { /* sem armazenamento */ }
};

const GoogleAccountPanel: React.FC<Props> = ({ report, reportLoading, reportError, onReloadReport, range, showValues }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(() => readLS(OPEN_KEY) === '1');
  const [tab, setTab] = useState<TabKey>(() => {
    return readLS(TAB_KEY) === 'callouts' ? 'callouts' : 'bidding';
  });

  const [bidding, setBidding] = useState<GoogleBiddingRow[] | null>(null);
  const [biddingLoading, setBiddingLoading] = useState(false);
  const [biddingError, setBiddingError] = useState<string | null>(null);
  const [biddingKey, setBiddingKey] = useState('');

  const [callouts, setCallouts] = useState<GoogleCallout[] | null>(null);
  const [calloutsLoading, setCalloutsLoading] = useState(false);
  const [calloutsError, setCalloutsError] = useState<string | null>(null);
  const [newCallout, setNewCallout] = useState('');
  const [savingCallout, setSavingCallout] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);

  const currency = report?.account?.currency || 'BRL';
  const money = (v: any) => {
    if (!showValues) return '•••••';
    try {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(num(v));
    } catch {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num(v));
    }
  };
  const moneyOrDash = (v: any) => (num(v) > 0 ? money(v) : '—');
  const int = (v: any) => new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(num(v));
  const dec = (v: any) => new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(num(v));

  const statusLabel = (s: string) => {
    switch ((s || '').toUpperCase()) {
      case 'ENABLED': return t('campaigns.google.status.enabled', 'Ativa');
      case 'PAUSED': return t('campaigns.google.status.paused', 'Pausada');
      case 'REMOVED': return t('campaigns.google.status.removed', 'Removida');
      case 'UNKNOWN': return t('campaigns.google.status.unknown', 'Desconhecido');
      case 'UNSPECIFIED': return t('campaigns.google.status.unspecified', 'Não especificado');
      default: return s || '—';
    }
  };
  const statusClass = (s: string) => {
    switch ((s || '').toUpperCase()) {
      case 'ENABLED': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'PAUSED': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'REMOVED': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    }
  };
  const StatusPill: React.FC<{ s: string }> = ({ s }) => (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusClass(s)}`}>{statusLabel(s)}</span>
  );

  const biddingTypeLabel = (type: string) => {
    switch ((type || '').toUpperCase()) {
      case 'TARGET_CPA': return t('campaigns.google.bidType.targetCpa', 'CPA desejado');
      case 'TARGET_ROAS': return t('campaigns.google.bidType.targetRoas', 'ROAS desejado');
      case 'MAXIMIZE_CONVERSIONS': return t('campaigns.google.bidType.maxConversions', 'Maximizar conversões');
      case 'MAXIMIZE_CONVERSION_VALUE': return t('campaigns.google.bidType.maxConversionValue', 'Maximizar valor da conversão');
      case 'MANUAL_CPC': return t('campaigns.google.bidType.manualCpc', 'CPC manual');
      case 'ENHANCED_CPC': return t('campaigns.google.bidType.enhancedCpc', 'CPC otimizado');
      case 'TARGET_SPEND': return t('campaigns.google.bidType.targetSpend', 'Maximizar cliques');
      case 'TARGET_IMPRESSION_SHARE': return t('campaigns.google.bidType.targetImpressionShare', 'Parcela de impressões desejada');
      case 'MANUAL_CPM': return t('campaigns.google.bidType.manualCpm', 'CPM manual');
      case 'MANUAL_CPV': return t('campaigns.google.bidType.manualCpv', 'CPV manual');
      case 'TARGET_CPM': return t('campaigns.google.bidType.targetCpm', 'CPM desejado');
      default: return type || '—';
    }
  };

  const toggleOpen = () => {
    setOpen((o) => { writeLS(OPEN_KEY, o ? '0' : '1'); return !o; });
  };
  const selectTab = (k: TabKey) => { setTab(k); writeLS(TAB_KEY, k); };

  const loadBidding = useCallback(async (force = false) => {
    if (!range) return;
    const key = `${range.startDate}|${range.endDate}`;
    if (!force && key === biddingKey) return;
    setBiddingKey(key);
    setBiddingLoading(true);
    setBiddingError(null);
    try {
      const res = await api.get('/campaigns/google/bidding-report', { params: { start: range.startDate, end: range.endDate } });
      const data = res.data?.data ?? res.data;
      setBidding(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setBidding(null);
      setBiddingError(errorMessage(err, t('campaigns.google.biddingError', 'Não foi possível carregar as estratégias de lance.')));
    } finally {
      setBiddingLoading(false);
    }
  }, [range, biddingKey, t]);

  const loadCallouts = useCallback(async () => {
    setCalloutsLoading(true);
    setCalloutsError(null);
    try {
      const res = await api.get('/campaigns/google/account-callouts');
      const data = res.data?.data ?? res.data;
      setCallouts(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setCallouts(null);
      setCalloutsError(errorMessage(err, t('campaigns.google.calloutsError', 'Não foi possível carregar as frases de destaque.')));
    } finally {
      setCalloutsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!open) return;
    if (tab === 'bidding') loadBidding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, range?.startDate, range?.endDate]);

  useEffect(() => {
    if (open && tab === 'callouts' && callouts === null && !calloutsLoading && !calloutsError) loadCallouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  const addCallout = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = newCallout.trim();
    if (!text || text.length > CALLOUT_MAX) return;
    setSavingCallout(true);
    try {
      await api.post('/campaigns/google/account-callouts', { text });
      toast.success(t('campaigns.google.calloutAdded', 'Frase de destaque adicionada'));
      setNewCallout('');
      await loadCallouts();
    } catch (err: any) {
      toast.error(errorMessage(err, t('campaigns.google.calloutAddError', 'Não foi possível adicionar a frase.')));
    } finally {
      setSavingCallout(false);
    }
  };

  const removeCallout = async (c: GoogleCallout) => {
    if (!window.confirm(t('campaigns.google.calloutConfirmRemove', 'Remover a frase "{{text}}" da conta?', { text: c.text }) as string)) return;
    setRemoving(c.resource_name);
    try {
      await api.delete('/campaigns/google/account-callouts', { data: { resource_name: c.resource_name } });
      toast.success(t('campaigns.google.calloutRemoved', 'Frase de destaque removida'));
      setCallouts((list) => (list ? list.filter((x) => x.resource_name !== c.resource_name) : list));
    } catch (err: any) {
      toast.error(errorMessage(err, t('campaigns.google.calloutRemoveError', 'Não foi possível remover a frase.')));
    } finally {
      setRemoving(null);
    }
  };

  const ErrorBox: React.FC<{ msg: string; onRetry: () => void }> = ({ msg, onRetry }) => (
    <div className="flex items-start gap-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-300">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <span className="flex-1">{msg}</span>
      <button onClick={onRetry} className="shrink-0 font-medium underline hover:no-underline">{t('campaigns.google.retry', 'Tentar de novo')}</button>
    </div>
  );
  const Loading = () => (
    <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400">
      <Loader2 className="w-4 h-4 animate-spin" /> {t('campaigns.google.loading', 'Carregando dados do Google Ads...')}
    </div>
  );
  const Empty: React.FC<{ msg: string }> = ({ msg }) => (
    <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">{msg}</p>
  );

  const th = 'px-3 py-2 text-right whitespace-nowrap';
  const td = 'px-3 py-2 text-right whitespace-nowrap text-gray-700 dark:text-gray-300';

  const renderBidding = () => {
    if (biddingLoading && !bidding) return <Loading />;
    if (biddingError) return <ErrorBox msg={biddingError} onRetry={() => loadBidding(true)} />;
    if (!bidding || bidding.length === 0) return <Empty msg={t('campaigns.google.noBidding', 'Nenhuma estratégia de lance com dados no período.')} />;
    return (
      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
              <th className="px-3 py-2 text-left">{t('campaigns.google.col.strategy', 'Estratégia')}</th>
              <th className="px-3 py-2 text-left">{t('campaigns.google.col.type', 'Tipo')}</th>
              <th className="px-3 py-2 text-center">{t('campaigns.google.col.status', 'Status')}</th>
              <th className={th}>{t('campaigns.google.col.clicks', 'Cliques')}</th>
              <th className={th}>{t('campaigns.google.col.impressions', 'Impressões')}</th>
              <th className={th}>{t('campaigns.google.col.cost', 'Custo')}</th>
              <th className={th}>{t('campaigns.google.col.costPerConv', 'Custo/conv.')}</th>
              <th className={th}>{t('campaigns.google.col.avgCpc', 'CPC médio')}</th>
              <th className={th}>{t('campaigns.google.col.conversions', 'Conversões')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {bidding.map((b, i) => (
              <tr key={`${b.kind}-${b.id || b.type}-${i}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="px-3 py-2 text-left text-gray-900 dark:text-white min-w-[200px]">
                  <div className="font-medium">
                    {b.kind === 'portfolio' ? (b.name || '—') : t('campaigns.google.standardStrategy', 'Padrão')}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {b.kind === 'portfolio' ? t('campaigns.google.portfolio', 'Estratégia de portfólio') : t('campaigns.google.standardHint', 'Definida na própria campanha')}
                    {b.campaigns != null && <> · {t('campaigns.google.campaignsCount', '{{count}} campanha(s)', { count: b.campaigns })}</>}
                  </div>
                </td>
                <td className="px-3 py-2 text-left text-gray-700 dark:text-gray-300 whitespace-nowrap">{biddingTypeLabel(b.type)}</td>
                <td className="px-3 py-2 text-center">{b.status ? <StatusPill s={b.status} /> : '—'}</td>
                <td className={td}>{int(b.clicks)}</td>
                <td className={td}>{int(b.impressions)}</td>
                <td className={td}>{money(b.cost)}</td>
                <td className={td}>{moneyOrDash(b.cost_per_conversion)}</td>
                <td className={td}>{moneyOrDash(b.average_cpc)}</td>
                <td className={td}>{dec(b.conversions)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCallouts = () => {
    const len = newCallout.length;
    const tooLong = len > CALLOUT_MAX;
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('campaigns.google.calloutsHint', 'Estas frases de destaque valem para a conta inteira e aparecem em todas as campanhas de Pesquisa dela.')}
        </p>
        <form onSubmit={addCallout} className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <input
              value={newCallout}
              onChange={(e) => setNewCallout(e.target.value)}
              maxLength={CALLOUT_MAX + 10}
              placeholder={t('campaigns.google.calloutPlaceholder', 'Ex.: Frete grátis') as string}
              className={`w-full pl-3 pr-16 py-2 rounded-lg border bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${tooLong ? 'border-red-400 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
              aria-label={t('campaigns.google.calloutNew', 'Nova frase de destaque') as string}
            />
            <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${tooLong ? 'text-red-500' : 'text-gray-400'}`}>{len}/{CALLOUT_MAX}</span>
          </div>
          <button
            type="submit"
            disabled={savingCallout || !newCallout.trim() || tooLong}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingCallout ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {t('campaigns.google.calloutAdd', 'Adicionar')}
          </button>
        </form>
        {calloutsLoading && !callouts ? <Loading /> : calloutsError ? (
          <ErrorBox msg={calloutsError} onRetry={loadCallouts} />
        ) : !callouts || callouts.length === 0 ? (
          <Empty msg={t('campaigns.google.noCallouts', 'Nenhuma frase de destaque na conta.')} />
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700 rounded-xl border border-gray-200 dark:border-gray-700">
            {callouts.map((c) => (
              <li key={c.resource_name} className="flex items-center justify-between gap-3 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{c.text}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('campaigns.google.assetId', 'Recurso')} {c.asset_id}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {c.status && <StatusPill s={c.status} />}
                  <button
                    onClick={() => removeCallout(c)}
                    disabled={removing === c.resource_name}
                    className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                    title={t('campaigns.google.calloutRemove', 'Remover') as string}
                  >
                    {removing === c.resource_name ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const tabs: Array<{ key: TabKey; label: string }> = [
    { key: 'bidding', label: t('campaigns.google.tabBidding', 'Estratégias de lance') },
    { key: 'callouts', label: t('campaigns.google.tabCallouts', 'Frases de destaque da conta') },
  ];

  const refresh = () => {
    onReloadReport();
    if (!open) return;
    if (tab === 'bidding') loadBidding(true);
    else loadCallouts();
  };
  const busy = reportLoading || (open && (tab === 'bidding' ? biddingLoading : calloutsLoading));

  // Números da conta inteira (o Google pede "todas as conversões" no nível da conta).
  const a = report?.account;
  const kpis: Array<[string, string]> = a ? [
    [t('campaigns.google.kpi.clicks', 'Cliques'), int(a.clicks)],
    [t('campaigns.google.kpi.impressions', 'Impressões'), int(a.impressions)],
    [t('campaigns.google.kpi.cost', 'Custo'), money(a.cost)],
    [t('campaigns.google.kpi.conversions', 'Conversões'), dec(a.conversions)],
    [t('campaigns.google.kpi.allConversions', 'Todas as conversões'), dec(a.all_conversions)],
  ] : [];

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm" id="google-ads-account-panel">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <button onClick={toggleOpen} className="flex items-center gap-2 min-w-0 text-left" aria-expanded={open}>
          <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z" fill="#4285F4"/>
          </svg>
          <span className="font-semibold text-gray-900 dark:text-white truncate">{t('campaigns.google.panelTitle', 'Conta Google Ads')}</span>
          {report?.account?.name && <span className="hidden sm:inline text-sm text-gray-500 dark:text-gray-400 truncate">· {report.account.name}</span>}
          <ChevronDown className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        <button
          onClick={refresh}
          disabled={busy}
          className="p-2 rounded-lg text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
          title={t('campaigns.google.refresh', 'Atualizar') as string}
        >
          <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <div className="px-4 pb-3 -mt-1">
        {reportError ? (
          <ErrorBox msg={reportError} onRetry={onReloadReport} />
        ) : reportLoading && !report ? (
          <p className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('campaigns.google.loading', 'Carregando dados do Google Ads...')}</p>
        ) : a ? (
          <dl className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
            {kpis.map(([label, value]) => (
              <div key={label} className="flex items-baseline gap-1.5">
                <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
                <dd className="font-semibold text-gray-900 dark:text-white">{value}</dd>
              </div>
            ))}
            {range && <div className="text-xs text-gray-400 dark:text-gray-500 self-center">{range.startDate} → {range.endDate}</div>}
          </dl>
        ) : null}
      </div>
      {open && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          <div className="tm-tabs flex gap-1 overflow-x-auto overflow-y-hidden px-3 shadow-[inset_0_-1px_0_#e5e7eb] dark:shadow-[inset_0_-1px_0_#374151]" role="tablist">
            {tabs.map((tb) => (
              <button
                key={tb.key}
                onClick={() => selectTab(tb.key)}
                role="tab"
                aria-selected={tab === tb.key}
                className={`px-3 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab === tb.key ? 'border-primary-600 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}
              >
                {tb.label}
              </button>
            ))}
          </div>
          <div className="p-3 sm:p-4">
            {tab === 'bidding' && renderBidding()}
            {tab === 'callouts' && renderCallouts()}
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleAccountPanel;
